/**
 * section-reveal.js
 *
 * Staggered scroll-reveal for the sections inside #MainContent.
 *
 * Each top-level section is the IntersectionObserver trigger, but the elements
 * that actually animate are the content rows found underneath it - a section
 * never fades as a whole on top of its own children fading, which is what makes
 * stacked reveals look muddy.
 *
 * Finding those rows means walking past this theme's wrapper chain
 * (.section > .custom-section-content > .layout-panel-flex > ...) while ignoring
 * decorative empties like .section-background, and stopping at any component
 * that drives its own transforms, so a slider arrives as one piece instead of
 * having its arrows and nav dots animate independently.
 *
 * Paired with section-reveal.css, bootstrapped by snippets/section-reveal.liquid.
 */
(function () {
  var root = document.documentElement;

  // Tell the inline bootstrap we made it, so it stops trying to un-hide the page.
  if (window.sectionRevealFailsafe) {
    clearTimeout(window.sectionRevealFailsafe);
    window.sectionRevealFailsafe = null;
  }

  if (!root.classList.contains('section-reveal-enabled')) return;

  var MAX_UNWRAP_DEPTH = 8;
  var MAX_STAGGER_INDEX = 9;
  var GRID_MIN_CHILDREN = 3;
  var CLEANUP_MS = 2800;

  /* Components that drive their own transforms - never descend into these. */
  var OPAQUE = [
    'slideshow-component',
    'layered-slideshow',
    'media-gallery',
    'marquee-component',
    'video-background',
    'model-viewer',
    'iframe',
    'video',
    '[class*="slideshow"]',
    '[class*="slider"]',
    '[class*="carousel"]',
    '[class*="marquee"]',
    '[class*="swiper"]',
    '[class*="splide"]',
    '[class*="flickity"]',
  ].join(',');

  /* Elements that count as content even when they hold no text. */
  var MEANINGFUL =
    'img,picture,svg,video,iframe,canvas,a,button,input,select,textarea,model-viewer';

  var SKIP_TAGS = { SCRIPT: 1, STYLE: 1, TEMPLATE: 1, NOSCRIPT: 1, LINK: 1 };

  var observer = null;

  function matches(element, selector) {
    return typeof element.matches === 'function' && element.matches(selector);
  }

  function elementChildren(element) {
    var out = [];
    for (var i = 0; i < element.children.length; i++) {
      var child = element.children[i];
      if (child instanceof HTMLElement && !SKIP_TAGS[child.tagName]) out.push(child);
    }
    return out;
  }

  /* Empty decorative layers must not soak up a stagger slot of their own. */
  function isMeaningful(element) {
    if (element.textContent && element.textContent.trim().length > 0) return true;
    if (matches(element, MEANINGFUL)) return true;
    return element.querySelector(MEANINGFUL) !== null;
  }

  function meaningfulChildren(element) {
    return elementChildren(element).filter(isMeaningful);
  }

  /* Transforming a sticky or fixed element breaks how it is positioned. */
  function isTransformSafe(element) {
    var position = getComputedStyle(element).position;
    return position !== 'sticky' && position !== 'fixed';
  }

  function isGridLike(element) {
    var display = getComputedStyle(element).display;
    return (
      display === 'grid' ||
      display === 'inline-grid' ||
      display === 'flex' ||
      display === 'inline-flex'
    );
  }

  /* Step past single-child wrappers so the stagger lands on real content. */
  function unwrap(element) {
    var current = element;

    for (var depth = 0; depth < MAX_UNWRAP_DEPTH; depth++) {
      var children = meaningfulChildren(current);
      if (children.length !== 1 || matches(children[0], OPAQUE)) break;
      current = children[0];
    }

    return current;
  }

  /* The animation units for a section, and whether they read as a card grid. */
  function resolveUnits(section) {
    var container = unwrap(section);
    var children = meaningfulChildren(container);

    // Fully unwrapped down to a single element: animate that element itself.
    if (children.length === 0) return [{ element: container, kind: 'row' }];

    var kind =
      children.length >= GRID_MIN_CHILDREN && isGridLike(container) ? 'card' : 'row';

    return children.map(function (child) {
      return { element: child, kind: kind };
    });
  }

  function prepare(section) {
    if (section.dataset.revealState) return;

    var units = resolveUnits(section).filter(function (unit) {
      return isTransformSafe(unit.element);
    });

    if (units.length === 0) {
      section.dataset.revealState = 'done';
      return;
    }

    units.forEach(function (unit, index) {
      unit.element.dataset.revealItem = 'pending';
      unit.element.dataset.revealUnit = unit.kind;
      unit.element.style.setProperty(
        '--reveal-index',
        String(Math.min(index, MAX_STAGGER_INDEX))
      );
    });

    section.revealUnits = units;
    section.dataset.revealState = 'pending';
  }

  /* Hand the elements back untouched so hover transforms are not pinned down. */
  function cleanup(section) {
    (section.revealUnits || []).forEach(function (unit) {
      delete unit.element.dataset.revealItem;
      delete unit.element.dataset.revealUnit;
      unit.element.style.removeProperty('--reveal-index');
    });

    section.revealUnits = null;
    section.dataset.revealState = 'done';
  }

  function reveal(section) {
    if (!section || !section.dataset) return;
    if (!section.dataset.revealState) prepare(section);
    if (section.dataset.revealState !== 'pending') return;

    section.dataset.revealState = 'in';

    (section.revealUnits || []).forEach(function (unit) {
      unit.element.dataset.revealItem = 'in';
    });

    setTimeout(function () {
      cleanup(section);
    }, CLEANUP_MS);
  }

  function register(section, index) {
    if (index === 0) section.dataset.revealMode = 'lead';

    prepare(section);
    if (section.dataset.revealState !== 'pending') return;

    observer.observe(section);
  }

  function topLevelSections() {
    var main = document.querySelector('#MainContent');
    if (!main) return [];
    return Array.prototype.slice.call(
      main.querySelectorAll(':scope > .shopify-section')
    );
  }

  function registerAll() {
    topLevelSections().forEach(register);
  }

  function init() {
    var main = document.querySelector('#MainContent');
    if (!main) return;

    observer = new IntersectionObserver(
      function (entries) {
        entries.forEach(function (entry) {
          if (!entry.isIntersecting) return;
          observer.unobserve(entry.target);
          reveal(entry.target);
        });
      },
      { rootMargin: '0px 0px -10% 0px', threshold: 0 }
    );

    registerAll();

    /*
     * Apps and the theme editor can inject sections after load. Without this
     * they would inherit the pre-paint hidden state and never come back.
     */
    new MutationObserver(registerAll).observe(main, { childList: true });

    document.addEventListener('shopify:section:load', function (event) {
      var section = event.target;
      if (!section || !section.classList || !section.classList.contains('shopify-section')) {
        return;
      }
      delete section.dataset.revealState;
      register(section, topLevelSections().indexOf(section));
    });

    /* Editing a section should show it immediately, mid-animation or not. */
    document.addEventListener('shopify:section:select', function (event) {
      reveal(event.target);
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
